import type { DomainDetailDto } from "@bafe/api-client";
import { OPERATIONAL_DEFAULTS } from "@bafe/config";
import { Button, Card, Field, InlineNotice, Input, PageHeader, Section, StatusPill, useToast } from "@bafe/ui";
import { formatBytes } from "@bafe/validation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { ErrorPanel } from "../../components/ErrorPanel";
import { LinkButton } from "../../components/LinkButton";
import { PasswordField } from "../../components/PasswordField";
import { QueryState } from "../../components/QueryState";
import { api } from "../../lib/api";
import { formatCount } from "../../lib/format";
import { queryKeys } from "../../lib/query";
import { MIGRATION_STATUS } from "../../lib/status";

const RETENTION_DAYS = OPERATIONAL_DEFAULTS.domain_removal_retention_days;

export function DomainRemove() {
	const { id = "" } = useParams();

	const domain = useQuery({
		queryKey: queryKeys.domain(id),
		queryFn: () => api.getDomain(id),
		enabled: id !== "",
	});

	return (
		<div className="flex flex-col gap-6">
			<QueryState query={domain} errorTitle="We could not load this domain.">
				{(detail) => <RemoveForm detail={detail} />}
			</QueryState>
		</div>
	);
}

function RemoveForm({ detail }: { detail: DomainDetailDto }) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { toast } = useToast();

	const [password, setPassword] = useState("");
	const [typedDomain, setTypedDomain] = useState("");

	const migrations = useQuery({ queryKey: queryKeys.migrations, queryFn: () => api.listMigrations() });
	const domainMigrations = (migrations.data ?? []).filter((migration) =>
		migration.mailboxAddress.toLowerCase().endsWith(`@${detail.domainName.toLowerCase()}`),
	);
	const activeMigrations = domainMigrations.filter(
		(migration) =>
			migration.status === "queued" || migration.status === "migrating" || migration.status === "retrying",
	);

	const remove = useMutation({
		mutationFn: async () => {
			// Re-authentication first, so a wrong password never reaches the
			// destructive call (spec §67).
			await api.reauthenticate(password);
			await api.removeDomain(detail.id, typedDomain.trim());
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.domains });
			void queryClient.invalidateQueries({ queryKey: queryKeys.overview });
			toast({
				tone: "success",
				message: `${detail.domainName} is queued for removal. We will email you when it is finished.`,
			});
			void navigate("/manage/domains", { replace: true });
		},
	});

	const confirmed = typedDomain.trim().toLowerCase() === detail.domainName.toLowerCase();

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!confirmed || password.length === 0) return;
		remove.mutate();
	}

	return (
		<>
			<PageHeader
				title={`Remove ${detail.domainName}`}
				description="This stops mail for this domain and schedules everything on it for deletion."
				breadcrumb={
					<Link
						to={`/manage/domains/${detail.id}`}
						className="text-sm text-(--text-secondary) underline underline-offset-2 hover:text-(--text-primary)"
					>
						{detail.domainName}
					</Link>
				}
			/>

			<Section title="What this affects">
				<Card className="flex flex-col gap-3">
					<Impact
						label="Email accounts"
						value={formatCount(detail.mailboxCount, "email account", "email accounts")}
						note="They stop receiving mail immediately and are scheduled for deletion with the domain."
					/>
					<Impact
						label="Aliases"
						value={formatCount(detail.aliasCount, "alias", "aliases")}
						note="Forwarding addresses on this domain stop working."
					/>
					<Impact
						label="Stored mail"
						value={formatBytes(detail.usedBytes)}
						note="This space returns to your plan once the retention window ends."
					/>
					<Impact
						label="Migrations"
						value={
							domainMigrations.length === 0
								? "None"
								: formatCount(domainMigrations.length, "migration", "migrations")
						}
						note={
							activeMigrations.length > 0
								? "A migration is still running. Removing the domain cancels it."
								: "No migration is running for this domain."
						}
					/>

					{domainMigrations.length > 0 && (
						<ul className="flex flex-col gap-2 border-t border-(--border-subtle) pt-3">
							{domainMigrations.map((migration) => (
								<li key={migration.id} className="flex flex-wrap items-center justify-between gap-2">
									<span className="text-sm text-(--text-primary)">{migration.mailboxAddress}</span>
									<StatusPill tone={MIGRATION_STATUS[migration.status].tone}>
										{MIGRATION_STATUS[migration.status].label}
									</StatusPill>
								</li>
							))}
						</ul>
					)}
				</Card>
			</Section>

			<InlineNotice tone="caution" title={`You have ${RETENTION_DAYS} days to change your mind`}>
				Mail on this domain is kept for {RETENTION_DAYS} days after removal. Contact support within that
				window and we can restore it. After that it is permanently deleted and cannot be recovered.
			</InlineNotice>

			<InlineNotice tone="info" title="Removal happens in the background">
				When you confirm, Key queues the work. It usually finishes within a few minutes, and you can leave
				this page. Mail stops arriving as soon as the job starts.
			</InlineNotice>

			<Section title="Confirm">
				<form className="flex max-w-lg flex-col gap-4" onSubmit={submit} noValidate>
					<PasswordField
						label="Your Key password"
						value={password}
						onChange={setPassword}
						autoComplete="current-password"
						help="We ask again because this cannot be undone from the control panel."
						required
					/>

					<Field
						label={`Type ${detail.domainName} to confirm`}
						error={
							typedDomain.length > 0 && !confirmed ? "This does not match the domain name." : null
						}
						required
					>
						{({ id, describedBy, invalid }) => (
							<Input
								id={id}
								value={typedDomain}
								required
								autoComplete="off"
								autoCapitalize="none"
								spellCheck={false}
								className="text-dns"
								aria-describedby={describedBy}
								aria-invalid={invalid}
								onChange={(event) => setTypedDomain(event.target.value)}
							/>
						)}
					</Field>

					{remove.isError && <ErrorPanel error={remove.error} title="We could not remove this domain." />}

					<div className="flex flex-wrap gap-2">
						<Button
							type="submit"
							variant="danger"
							disabled={!confirmed || password.length === 0}
							pending={remove.isPending}
							pendingLabel="Queuing removal…"
						>
							Remove domain
						</Button>
						<LinkButton to={`/manage/domains/${detail.id}`} variant="ghost">
							Keep this domain
						</LinkButton>
					</div>
				</form>
			</Section>
		</>
	);
}

function Impact({ label, value, note }: { label: string; value: string; note: string }) {
	return (
		<div className="flex flex-col gap-0.5">
			<div className="flex flex-wrap items-baseline justify-between gap-2">
				<span className="text-sm font-medium text-(--text-primary)">{label}</span>
				<span className="text-sm text-(--text-primary)">{value}</span>
			</div>
			<p className="text-sm text-(--text-secondary)">{note}</p>
		</div>
	);
}
